import React, { Component } from 'react';
import { NavLink } from 'react-router-dom';

import { Wrapper, List, Item, ItemContainer } from './styled/HeaderMenuStyled';
import Icon from '../../common/Icon';
import ClickOutside from '../../common/ClickOutside';

import { colors, routes, config } from '../../../utils/utils';
import { DEFAULT_SNOOZE_TIMER } from '../../../utils/snooze';
import { IS_LEGACY_LISTENER_MODE } from '../../../config/electron';
import { CALL_SIMULATOR_PAGE } from '../../../utils/routes';
import { openUrl } from '../../../utils/browser';

const { TRAINER_PAGE, SETTINGS_PAGE } = routes;

interface MenuProps {
    localization: { getText: (key: string) => string };
    disabled: boolean;
    displaySnoozingControls: boolean;
    isTrainerActive: boolean;
    resumeTrainer: () => void;
    snoozeTrainer: (seconds: number) => void;
    doLogout?: () => void;
    setInfinitySnooze: () => void;
    showDashboardLink: boolean;
    authToken?: string;
    authWithPasswordRequired: boolean;
    enableCallSimulator: boolean;
}

interface MenuState {
    open: boolean;
}

class Menu extends Component<MenuProps, MenuState> {
    static defaultProps: Partial<MenuProps> = {
        doLogout: undefined,
        authToken: undefined
    };

    state = {
        open: false
    };

    _toggleOpen = () => {
        if (this.props.disabled) {
            return;
        }
        this.setState((prevState: MenuState) => ({
            open: !prevState.open
        }));
    };

    _handleOuterClick = () => {
        this.setState({
            open: false
        });
    };

    _handleSnoozeClick = () => {
        const { resumeTrainer, isTrainerActive, snoozeTrainer } = this.props;

        if (isTrainerActive) {
            snoozeTrainer(DEFAULT_SNOOZE_TIMER);
        } else {
            resumeTrainer();
        }
    };

    _openDashboard = () => {
        const { authToken } = this.props;
        const dashboardUrl = config.getDashboardLink(process.env.NODE_ENV);
        const params = encodeURIComponent(authToken || '');
        openUrl(`${dashboardUrl}/login?token=${params}`, !IS_LEGACY_LISTENER_MODE ? '_blank' : '_self');
    };

    _deactivate = () => {
        this.props.setInfinitySnooze();
    };

    _logout = () => {
        if (this.props.doLogout) {
            this.props.doLogout();
        }
    };

    render() {
        const { open } = this.state;
        const {
            localization,
            disabled,
            displaySnoozingControls,
            isTrainerActive,
            showDashboardLink,
            authWithPasswordRequired,
            enableCallSimulator
        } = this.props;

        return (
            <ClickOutside onClickOutside={this._handleOuterClick}>
                <Wrapper $open={open} $disabled={disabled} onClick={this._toggleOpen}>
                    <Icon name="Menu" size={19} color={open ? colors.blue.palatinate_blue : colors.black.default} />
                    <List $visible={open}>
                        <NavLink to={TRAINER_PAGE}>
                            <Item>{localization.getText('header.menu.home')}</Item>
                        </NavLink>
                        {showDashboardLink && (
                            <Item onClick={this._openDashboard}>
                                <ItemContainer>
                                    <span>{localization.getText('header.menu.dashboard')}</span>
                                    <Icon name="Launch" />
                                </ItemContainer>
                            </Item>
                        )}
                        {displaySnoozingControls && (
                            <Item onClick={this._handleSnoozeClick}>
                                <ItemContainer>
                                    <span>
                                        {isTrainerActive
                                            ? localization.getText('header.menu.snooze')
                                            : localization.getText('header.menu.enable')}
                                    </span>
                                    <Icon name="Mic" />
                                </ItemContainer>
                            </Item>
                        )}
                        {displaySnoozingControls && (
                            <Item onClick={this._deactivate}>{localization.getText('trainer.inactive.deactivate')}</Item>
                        )}
                        <NavLink to={SETTINGS_PAGE}>
                            <Item>{localization.getText('header.menu.settings')}</Item>
                        </NavLink>
                        {enableCallSimulator && (
                            <NavLink to={CALL_SIMULATOR_PAGE}>
                                <Item>{localization.getText('header.menu.call_simulator')}</Item>
                            </NavLink>
                        )}
                        {authWithPasswordRequired && (
                            <Item onClick={this._logout}>
                                <ItemContainer>{localization.getText('header.menu.logout')}</ItemContainer>
                            </Item>
                        )}
                    </List>
                </Wrapper>
            </ClickOutside>
        );
    }
}

export default Menu;
